import { useEffect } from "react";
import type { View } from "@gooner/shared";
import { viewIdKind } from "@gooner/shared";
import type { SyncBus } from "../syncBus";
import type { RoomCtx } from "./useRoom";

/**
 * THE HOST MIRRORS. A room is the host's screen: their DisplayApp runs the real view
 * exactly as /screen does, and this is the one place it reports what it has —
 *
 *  - its VIEW, whole, on every change (the config it was handed, a hover-chrome edit, a
 *    quick setup's session-local tweak that no PUT would keep): the room's snapshot, which
 *    members' display sockets hear as `config`. Debounced: a volume drag lands a few a
 *    second. Never a wall — the server resolves those itself, and a signed wall would
 *    not fit a frame. Never while a scroll shows — that is mirrored by its pages
 *    (InfinityStage) and the hub ignores a view under one anyway.
 *  - every REVEAL on the local SyncBus, whatever the id: an authored region's, a scroll
 *    page's (`infinityRegionId`s come and go with the pages, so this subscribes to the
 *    bus as a whole rather than per id); and the whole board again whenever the room
 *    socket (re)connects, since a reveal is not queued while it is down and the tiles
 *    may have revealed before it opened.
 *  - its PAUSE. A member's own pause never leaves their screen.
 *
 * `focal` and `previewUrl` are stripped from mirrored items: a member's tile is driven
 * off `room_playing` (which keeps focal) and never loads a layout's clip itself.
 */
export function useRoomMirror({ room, view, syncBus, paused }: { room: RoomCtx | null; view: View | null; syncBus: SyncBus; paused: boolean }): void {
  const host = room?.role === "owner";
  const connected = room?.connected ?? false;
  const showing = room?.showing ?? null;

  useEffect(() => {
    if (!room || !host || !connected || !view || !showing) return;
    const kind = viewIdKind(showing);
    if (kind === "infinity" || kind === "wall") return;
    const t = setTimeout(() => room.sendView(slim(view)), 150);
    return () => clearTimeout(t);
  }, [room, host, connected, view, showing]);

  useEffect(() => {
    if (!room || !host || !connected) return;
    for (const id of syncBus.ids()) {
      const s = syncBus.get(id);
      if (s) room.sendReveal(id, s.item, s.src);
    }
    return syncBus.subscribeAll((id, s) => {
      if (s) room.sendReveal(id, s.item, s.src);
    });
  }, [room, host, connected, syncBus]);

  useEffect(() => {
    if (!room || !host || !connected) return;
    room.sendPause(paused);
  }, [room, host, connected, paused]);
}

/** The view with what a member's layout does not need — and what would not fit a frame. */
export function slim(view: View): View {
  return {
    ...view,
    regions: view.regions.map((r) => {
      const src = r.source;
      if (src.kind === "clip" && src.clip) {
        const { focal: _f, previewUrl: _p, ...clip } = src.clip as typeof src.clip & { focal?: unknown; previewUrl?: unknown };
        return { ...r, source: { kind: "clip" as const, clip } };
      }
      if (src.kind === "clips") {
        return {
          ...r,
          source: {
            kind: "clips" as const,
            clips: src.clips.map((c) => {
              const { focal: _f, previewUrl: _p, ...clip } = c as typeof c & { focal?: unknown; previewUrl?: unknown };
              return clip;
            }),
          },
        };
      }
      return r;
    }),
  };
}
